import { useState } from "react"
import "./PlaceConcerns.css"
import { getConcerns } from "../services/chaperonConnector"
import { useEffect } from "react"

export default function PlaceConcerns({ cardPlace }) {
    const [activeTab, setActiveTab] = useState("safety")
    const [expandedItem, setExpandedItem] = useState(null)
    const [concerns, setConcerns] = useState([])
    const [shouldHideComponent, setShouldHideComponent] = useState(false)

    const toggleAccordion = (index) => {
        setExpandedItem(expandedItem === index ? null : index)
    }

    useEffect(() => {
        const fetchConcerns = async () => {
            try {
                const fetchedConcerns = await getConcerns(cardPlace.country)
                const { structured: concernsData, shouldHide } = fetchedConcerns

                if (shouldHide) {
                    setShouldHideComponent(true)
                    return
                }

                setConcerns(concernsData)
                setShouldHideComponent(false)
            } catch (error) {
                console.error("Error fetching concerns:", error)
                setShouldHideComponent(true)
            }
        }

        if (cardPlace?.country) {
            fetchConcerns()
        }
    }, [cardPlace?.country])

    // Helper function to get icon for concern type
    const getIconForType = (type) => {
        const iconMap = {
            crime: "🚨",
            fraud: "💳",
            "spiked-food-and-drinks": "🥤",
            "womens-safety": "👩",
            demonstrations: "📢",
            terrorism: "💣",
            "landmines-and-unexploded-ordnance": "💥",
            "soccer-matches-and-sports-events": "⚽",
            "water-activities": "🏊",
            "mountain-activities": "⛰️",
            "power-outages": "⚡",
            "road-safety": "🚗",
            "public-transportation": "🚌",
            "air-travel": "✈️",
            drugs: "💊",
            identification: "🪪",
            photography: "�",
            "dual-citizenship": "🛂",
            "international-child-abduction": "👶",
            "2slgbtqi-persons": "🏳️‍🌈",
            driving: "🚙",
            money: "💰",
            "flooding-and-landslides": "🌊",
            earthquakes: "🌍",
            wildfires: "🔥",
            "air-pollution": "🌫️",
            "transfer-to-a-canadian-prison": "🏛️",
        }
        return iconMap[type] || "⚠️"
    }

    // Helper function to format concern type title
    const formatTitle = (type) => {
        return type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
    }

    // Process concerns data for the active category
    const getActiveTabData = () => {
        if (!concerns.length) return []

        const activeCategory = concerns.find(
            (cat) => cat.category === activeTab
        )
        if (!activeCategory) return []

        return activeCategory.types.map((typeGroup) => {
            const combinedContent = []

            typeGroup.concerns.forEach((concern) => {
                if (concern.concern_overview) {
                    const lastPeriodIndex =
                        concern.concern_overview.lastIndexOf(".")
                    if (
                        lastPeriodIndex !== -1 &&
                        lastPeriodIndex === concern.concern_overview.length - 1
                    ) {
                        const shortenedOverview =
                            concern.concern_overview.substring(
                                0,
                                lastPeriodIndex + 1
                            )
                        combinedContent.push(shortenedOverview)
                    } else if (lastPeriodIndex !== -1) {
                        const shortenedOverview =
                            concern.concern_overview.substring(
                                0,
                                lastPeriodIndex + 1
                            )
                        combinedContent.push(shortenedOverview)
                    }
                    // If no period found, exclude the overview altogether
                }
                if (concern.concern_details) {
                    // Check if details is different from overview (or if overview is null)
                    const isDifferentFromOverview =
                        !concern.concern_overview ||
                        concern.concern_details !== concern.concern_overview

                    if (isDifferentFromOverview) {
                        const lastPeriodIndex =
                            concern.concern_details.lastIndexOf(".")
                        if (
                            lastPeriodIndex !== -1 &&
                            lastPeriodIndex ===
                                concern.concern_details.length - 1
                        ) {
                            const shortenedDetails =
                                concern.concern_details.substring(
                                    0,
                                    lastPeriodIndex + 1
                                )
                            combinedContent.push(shortenedDetails)
                        } else if (lastPeriodIndex !== -1) {
                            const shortenedDetails =
                                concern.concern_details.substring(
                                    0,
                                    lastPeriodIndex + 1
                                )
                            combinedContent.push(shortenedDetails)
                        }
                        // If no period found, exclude the details altogether
                    }
                }
            })

            return {
                id: typeGroup.type,
                title: formatTitle(typeGroup.type),
                icon: getIconForType(typeGroup.type),
                level: typeGroup.level || "default", // Use default if no level
                content: combinedContent.filter(
                    (content) => content && content.trim()
                ),
            }
        })
    }

    const activeTabData = getActiveTabData()

    // Create tabs with dynamic counts
    const tabs = [
        {
            id: "safety",
            label: "Safety",
            count:
                concerns.find((cat) => cat.category === "safety")?.types
                    ?.length || 0,
        },
        {
            id: "legal",
            label: "Legal",
            count:
                concerns.find((cat) => cat.category === "legal")?.types
                    ?.length || 0,
        },
        {
            id: "environment",
            label: "Environmental",
            count:
                concerns.find((cat) => cat.category === "environment")?.types
                    ?.length || 0,
        },
        {
            id: "health",
            label: "Health",
            count:
                concerns.find((cat) => cat.category === "health")?.types
                    ?.length || 0,
        },
    ]

    // Hide component if there was an error fetching concerns
    if (shouldHideComponent) {
        return null
    }

    return (
        <div className="place-concerns">
            {/* Modern Tab Navigation */}
            <div className="concerns-tabs">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        className={`tab-button ${
                            activeTab === tab.id ? "active" : ""
                        }`}
                        onClick={() => setActiveTab(tab.id)}
                        type="button"
                    >
                        <span className="tab-label">{tab.label}</span>
                        {tab.count > 0 && (
                            <span className="tab-badge">{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="concerns-content">
                {activeTabData.length > 0 ? (
                    <div className="concerns-list">
                        <div className="concerns-accordion">
                            {activeTabData.map((concern, index) => (
                                <div
                                    key={concern.id}
                                    className={`concern-item ${
                                        concern.level === "default"
                                            ? ""
                                            : concern.level
                                    }`}
                                >
                                    <button
                                        className={`concern-header ${
                                            expandedItem === index
                                                ? "expanded"
                                                : ""
                                        }`}
                                        onClick={() => toggleAccordion(index)}
                                        type="button"
                                        aria-expanded={expandedItem === index}
                                    >
                                        <div className="concern-title">
                                            <span className="concern-icon">
                                                {concern.icon}
                                            </span>
                                            <span className="concern-name">
                                                {concern.title}
                                            </span>
                                        </div>
                                        <div className="concern-meta">
                                            <svg
                                                className={`chevron ${
                                                    expandedItem === index
                                                        ? "rotated"
                                                        : ""
                                                }`}
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <polyline points="6,9 12,15 18,9"></polyline>
                                            </svg>
                                        </div>
                                    </button>

                                    <div
                                        className={`concern-content ${
                                            expandedItem === index
                                                ? "expanded"
                                                : ""
                                        }`}
                                    >
                                        <div className="concern-body">
                                            {concern.content.length > 1 ? (
                                                <ul
                                                    style={{
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {concern.content.map(
                                                        (item, itemIndex) => (
                                                            <li key={itemIndex}>
                                                                {item}
                                                            </li>
                                                        )
                                                    )}
                                                </ul>
                                            ) : (
                                                <p
                                                    style={{
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {concern.content[0] ||
                                                        "No details available."}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="tab-content-placeholder">
                        <div className="placeholder-content">
                            <h3>
                                {activeTab === "safety" && "Safety Information"}
                                {activeTab === "legal" && "Legal Information"}
                                {activeTab === "environment" &&
                                    "Environmental Factors"}
                                {activeTab === "health" && "Health Factors"}
                            </h3>
                            <p>
                                {activeTab === "safety" &&
                                    "Safety concerns and considerations will be displayed here."}
                                {activeTab === "legal" &&
                                    "Legal considerations and requirements will be displayed here."}
                                {activeTab === "environment" &&
                                    "Environmental concerns and considerations will be displayed here."}
                                {activeTab === "health" &&
                                    "Health concerns and considerations will be displayed here."}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
